import shufflingMp3 from '../assets/oxidvideos-shuffling-deck-of-cards-522518.mp3';
import takingCardMp3 from "../assets/oxidvideos-taking-playing-card-522520.mp3";
import cardDealFlickWav from "../assets/card-deal-flick.wav";
import cardSlideDealWav from "../assets/571581__el_boss__playing-card-slide-right.wav";
class SoundManager {
  private muted: boolean = false;
  private hapticsEnabled: boolean = true;
  private audioCtx: AudioContext | null = null;
  private cardPlayCounter: number = 0;
  private cardCollectCounter: number = 0;
  private lastShuffleTime: number = 0;
  private pistiCounter: number = 0;
  private lastCardHoverTime: number = 0;

  private rawBuffers: { [key: string]: ArrayBuffer | null } = {
    shuffling: null,
    takingCard: null,
    cardDealFlick: null,
    cardDeal: null
  };
  private decodedBuffers: { [key: string]: AudioBuffer | null } = {
    shuffling: null,
    takingCard: null,
    cardDealFlick: null,
    cardDeal: null
  };
  private audioElements: { [key: string]: HTMLAudioElement } = {};
  private masterGainNode: GainNode | null = null;
  private masterCompressor: DynamicsCompressorNode | null = null;
  private masterWarmFilter: BiquadFilterNode | null = null;
  private masterPannerNode: StereoPannerNode | null = null;
  private musicMode: boolean = false;
  private panValue: number = 0;

  constructor() {
    try {
      const savedMuted = localStorage.getItem('soundMuted');
      if (savedMuted !== null) {
        this.muted = savedMuted === 'true';
      }
      const savedHaptics = localStorage.getItem('hapticsEnabled');
      if (savedHaptics !== null) {
        this.hapticsEnabled = savedHaptics === 'true';
      }
      const savedPan = localStorage.getItem('soundPan');
      if (savedPan !== null) {
        this.panValue = parseFloat(savedPan) || 0;
      }
    } catch {
      // In environments with restricted storage, use defaults
      this.muted = false;
      this.hapticsEnabled = true;
      this.musicMode = false;
      this.panValue = 0;
    }
    this.setupInteractionUnlock();
  }

  private setupInteractionUnlock() {
    if (typeof window === 'undefined') return;
    let unlocked = false;
    const unlock = () => {
      if (unlocked) return;
      unlocked = true;
      this.initCtx();
      this.preloadRawData();
      if (this.audioCtx && this.audioCtx.state === 'suspended') {
        this.audioCtx.resume().catch(() => {});
      }
      window.removeEventListener('click', unlock, true);
      window.removeEventListener('keydown', unlock, true);
      window.removeEventListener('touchstart', unlock, true);
      window.removeEventListener('pointerdown', unlock, true);
    };
    window.addEventListener('click', unlock, { capture: true, once: true });
    window.addEventListener('keydown', unlock, { capture: true, once: true });
    window.addEventListener('touchstart', unlock, { capture: true, passive: true, once: true });
    window.addEventListener('pointerdown', unlock, { capture: true, passive: true, once: true });

    // PWA & Background-Foreground Resume Listeners
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          if (this.audioCtx && this.audioCtx.state === 'suspended') {
            this.audioCtx.resume().catch(() => {});
          }
        }
      });
    }
    window.addEventListener('pageshow', () => {
      if (this.audioCtx && this.audioCtx.state === 'suspended') {
        this.audioCtx.resume().catch(() => {});
      }
    });

    // Sync muted state across windows/tabs
    window.addEventListener('storage', (e) => {
      if (e.key === 'soundMuted' && e.newValue !== null) {
        this.muted = e.newValue === 'true';
      }
      if (e.key === 'hapticsEnabled' && e.newValue !== null) {
        this.hapticsEnabled = e.newValue === 'true';
      }
    });
  }

  setHapticsEnabled(enabled: boolean) {
    this.hapticsEnabled = enabled;
    try {
      localStorage.setItem('hapticsEnabled', enabled.toString());
    } catch {}
  }

  isHapticsEnabled() {
    return this.hapticsEnabled;
  }

  private triggerHaptic(soundName: string) {
    if (!this.hapticsEnabled || typeof navigator === 'undefined' || !navigator.vibrate) return;
    try {
      switch (soundName) {
        case 'event_strong_play':
          navigator.vibrate([20, 30, 20]);
          break;
        case 'pisti':
        case 'event_pisti':
          navigator.vibrate([30, 40, 50, 40, 80]);
          break;
        case 'win':
        case 'event_game_win':
        case 'event_round_win':
          navigator.vibrate([40, 30, 40, 30, 40, 30, 100]);
          break;
        case 'lose':
        case 'event_lose':
          navigator.vibrate([60, 50, 80]);
          break;
      }
    } catch (e) {
      // Ignore vibration errors
    }
  }

  private playAudioElement(key: string, volume: number = 0.8) {
    try {
      const url =
        key === 'takingCard'
          ? takingCardMp3
          : key === 'shuffling'
          ? shufflingMp3
          : key === 'cardDeal'
          ? cardSlideDealWav
          : key === 'cardDealFlick'
          ? cardDealFlickWav
          : null;
      if (!url) return;
      const audio = new Audio(url);
      audio.volume = this.muted ? 0 : (this.musicMode ? Math.min(volume * 0.10, 0.08) : Math.min(volume * 0.30, 0.22));
      const promise = audio.play();
      if (promise && promise.catch) {
        promise.catch(() => {});
      }
    } catch (e) {
      // Ignore audio autoplay restrictions
    }
  }

  private preloaded: boolean = false;

  private async preloadRawData() {
    if (this.preloaded) return;
    this.preloaded = true;
    try {
      const urls = {
        shuffling: shufflingMp3,
        takingCard: takingCardMp3,
        cardDealFlick: cardDealFlickWav,
        cardDeal: cardSlideDealWav
      };
      if (typeof window !== 'undefined') {
        const shufflingAudio = new Audio(shufflingMp3);
        shufflingAudio.preload = 'auto';
        this.audioElements['shuffling'] = shufflingAudio;

        const takingCardAudio = new Audio(takingCardMp3);
        takingCardAudio.preload = 'auto';
        this.audioElements["takingCard"] = takingCardAudio;

        const cardDealFlickAudio = new Audio(cardDealFlickWav);
        cardDealFlickAudio.preload = "auto";
        this.audioElements["cardDealFlick"] = cardDealFlickAudio;

        const cardDealAudio = new Audio(cardSlideDealWav);
        cardDealAudio.preload = "auto";
        this.audioElements["cardDeal"] = cardDealAudio;
      }
      for (const [key, url] of Object.entries(urls)) {
        if (!url) continue;
        fetch(url)
          .then(res => {
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const contentType = res.headers.get('content-type');
            if (contentType && contentType.includes('text/html')) {
              throw new Error('Received HTML instead of audio file');
            }
            return res.arrayBuffer();
          })
          .then(arrayBuf => {
            if (arrayBuf.byteLength < 500) {
              throw new Error('Audio buffer too small or corrupt');
            }
            this.rawBuffers[key] = arrayBuf;
            if (this.audioCtx) {
              this.decodeBuffer(key);
            }
          })
          .catch(() => {
            // Quietly fallback to HTML5 Audio element or Web Audio procedural sound
            this.rawBuffers[key] = null;
          });
      }
    } catch {
      // Quiet fallback
    }
  }

  private decodeBuffer(key: string) {
    if (!this.audioCtx || !this.rawBuffers[key] || this.decodedBuffers[key]) return;
    try {
      const p = this.audioCtx.decodeAudioData(
        this.rawBuffers[key]!.slice(0),
        (decoded) => {
          this.decodedBuffers[key] = decoded;
        },
        () => {
          this.decodedBuffers[key] = null;
        }
      );
      if (p && p.catch) p.catch(() => {});
    } catch {
      this.decodedBuffers[key] = null;
    }
  }

  private playBuffer(
    key: string,
    t: number,
    volume: number = 0.5,
    rate: number = 1.0,
    crispFilter: boolean = true,
    pan: number = 0,
    maxDuration?: number
  ): boolean {
    if (!this.audioCtx) {
      this.initCtx();
    }
    if (this.audioCtx && this.decodedBuffers[key]) {
      try {
        if (this.audioCtx.state === 'suspended') {
          this.audioCtx.resume().catch(() => {});
        }
        const actualTime = Math.max(t, this.audioCtx.currentTime);
        const source = this.audioCtx.createBufferSource();
        source.buffer = this.decodedBuffers[key];
        source.playbackRate.setValueAtTime(rate, actualTime);
        const gain = this.audioCtx.createGain();
        gain.gain.setValueAtTime(volume, actualTime);

        if (maxDuration && maxDuration > 0) {
          gain.gain.setValueAtTime(volume, actualTime + maxDuration * 0.6);
          gain.gain.exponentialRampToValueAtTime(0.0001, actualTime + maxDuration);
          source.stop(actualTime + maxDuration);
        }

        let finalNode: AudioNode = gain;

        if (crispFilter && key === "takingCard") {
          // Clean, natural card EQ: gentle low-cut below 220Hz to remove boominess, slight 3.2kHz clarity boost
          const hpFilter = this.audioCtx.createBiquadFilter();
          hpFilter.type = "highpass";
          hpFilter.frequency.setValueAtTime(220, actualTime);
          const hsFilter = this.audioCtx.createBiquadFilter();
          hsFilter.type = "highshelf";
          hsFilter.frequency.setValueAtTime(3200, actualTime);
          hsFilter.gain.setValueAtTime(2.0, actualTime);
          source.connect(hpFilter);
          hpFilter.connect(hsFilter);
          hsFilter.connect(gain);
        } else {
          source.connect(gain);
        }

        // Stereo spatial panner node (-1.0 left to +1.0 right)
        if (pan !== 0 && typeof (this.audioCtx as any).createStereoPanner === "function") {
          const panner = (this.audioCtx as any).createStereoPanner();
          panner.pan.setValueAtTime(Math.max(-0.9, Math.min(0.9, pan)), actualTime);
          gain.connect(panner);
          finalNode = panner;
        }

        finalNode.connect(this.getMasterNode());
        source.start(actualTime);
        return true;
      } catch (e) {
        console.warn(`Buffer play error ${key}:`, e);
      }
    }
    
    // Fallback: trigger HTML5 audio element safely
    this.playAudioElement(key, volume);
    return false; // Return false so calling functions execute procedural Web Audio sound fallback!
  }

  /**
   * Master Acoustic Bus (Velvet Table Comfort Limiter & Warm Lowpass Filter)
   * Kullanıcı cihaz sesini son ses (%100) açsa dahi seslerin yumuşak, kısık ve rahatlatıcı
   * kalmasını garanti eder. Sivri dijital frekansları ve ani patlamaları süzüp sınırlar.
   */
  private getMasterNode(): AudioNode {
    if (!this.audioCtx) return null as any;
    if (this.masterGainNode && this.masterCompressor && this.masterWarmFilter) {
      return this.masterWarmFilter;
    }

    try {
      // 1. Kadife Çuha Alçak Geçiren Filtre (5800Hz üzeri tüm keskin/metalik sivrilikleri yumuşatır)
      this.masterWarmFilter = this.audioCtx.createBiquadFilter();
      this.masterWarmFilter.type = "lowpass";
      this.masterWarmFilter.frequency.setValueAtTime(5800, this.audioCtx.currentTime);
      this.masterWarmFilter.Q.setValueAtTime(0.65, this.audioCtx.currentTime);

      // 2. Kulak Koruyucu Dinamik Kompresör / Sınırlayıcı (ani tepe vuruşlarını ve üst üste binen kart seslerini yumuşatır)
      this.masterCompressor = this.audioCtx.createDynamicsCompressor();
      this.masterCompressor.threshold.setValueAtTime(-18, this.audioCtx.currentTime);
      this.masterCompressor.knee.setValueAtTime(12, this.audioCtx.currentTime);
      this.masterCompressor.ratio.setValueAtTime(4.5, this.audioCtx.currentTime);
      this.masterCompressor.attack.setValueAtTime(0.003, this.audioCtx.currentTime);
      this.masterCompressor.release.setValueAtTime(0.12, this.audioCtx.currentTime);

      // 3. Konfor Ana Çıkış Seviyesi (Müzik modunda 0.10, normalde 0.28)
      const baseGain = this.muted ? 0 : (this.musicMode ? 0.10 : 0.28);
      this.masterGainNode = this.audioCtx.createGain();
      this.masterGainNode.gain.setValueAtTime(baseGain, this.audioCtx.currentTime);

      // 4. Stereo Panner (Müzik modunda sağ kulağa hafif yönlendirme: 0.60)
      if (typeof this.audioCtx.createStereoPanner === 'function') {
        this.masterPannerNode = this.audioCtx.createStereoPanner();
        const initialPan = this.musicMode && this.panValue === 0 ? 0.60 : this.panValue;
        this.masterPannerNode.pan.setValueAtTime(initialPan, this.audioCtx.currentTime);
      }

      // Bağlantı sırası: masterWarmFilter -> masterCompressor -> masterGainNode -> (masterPannerNode) -> destination
      this.masterWarmFilter.connect(this.masterCompressor);
      this.masterCompressor.connect(this.masterGainNode);
      if (this.masterPannerNode) {
        this.masterGainNode.connect(this.masterPannerNode);
        this.masterPannerNode.connect(this.audioCtx.destination);
      } else {
        this.masterGainNode.connect(this.audioCtx.destination);
      }

      return this.masterWarmFilter;
    } catch {
      return this.audioCtx.destination;
    }
  }

  private initCtx() {
    if (!this.audioCtx) {
      try {
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        // Balanced latency prevents exclusive hardware lock which avoids YouTube audio renderer clashes
        try {
          this.audioCtx = new AudioContextClass({
            latencyHint: 'balanced',
          });
        } catch {
          this.audioCtx = new AudioContextClass();
        }
        this.masterGainNode = null;
        this.masterCompressor = null;
        this.masterWarmFilter = null;
        this.masterPannerNode = null;
        // Önceden indirilmiş tamponları şimdi çöz (decode)
        for (const key of Object.keys(this.rawBuffers)) {
          this.decodeBuffer(key);
        }
      } catch (e) {
        console.log("AudioContext state:", e);
      }
    }
    if (this.audioCtx && this.audioCtx.state === 'suspended') {
      try {
        const p = this.audioCtx.resume();
        if (p && p.catch) p.catch(() => {});
      } catch (e) {}
    }
  }

  public updateMasterNodes() {
    if (!this.audioCtx) return;
    try {
      const now = this.audioCtx.currentTime;
      if (this.masterGainNode) {
        const targetGain = this.muted ? 0 : (this.musicMode ? 0.10 : 0.28);
        this.masterGainNode.gain.cancelScheduledValues(now);
        this.masterGainNode.gain.setTargetAtTime(targetGain, now, 0.05);
      }
      if (this.masterPannerNode) {
        const targetPan = this.musicMode && this.panValue === 0 ? 0.60 : this.panValue;
        this.masterPannerNode.pan.cancelScheduledValues(now);
        this.masterPannerNode.pan.setTargetAtTime(targetPan, now, 0.05);
      }
    } catch {}
  }

  public resume() {
    if (!this.audioCtx) {
      this.initCtx();
    }
    if (this.audioCtx && this.audioCtx.state === 'suspended') {
      try {
        const p = this.audioCtx.resume();
        if (p && p.catch) p.catch(() => {});
      } catch (e) {}
    }
  }

  setMuted(muted: boolean) {
    this.muted = muted;
    try {
      localStorage.setItem('soundMuted', muted.toString());
    } catch {}
    this.updateMasterNodes();
  }

  isMuted() {
    return this.muted;
  }

  setMusicMode(enabled: boolean) {
    this.musicMode = enabled;
    this.updateMasterNodes();
    window.dispatchEvent(new CustomEvent('musicModeToggle', { detail: { enabled } }));
  }

  isMusicMode(): boolean {
    return this.musicMode;
  }

  setPan(pan: number) {
    this.panValue = Math.max(-1, Math.min(1, pan));
    try {
      localStorage.setItem('soundPan', this.panValue.toString());
    } catch {}
    this.updateMasterNodes();
  }

  getPan(): number {
    return this.panValue;
  }

  play(soundName: 'click' | 'deal' | 'cardPlay' | 'collect' | 'pisti' | 'win' | 'lose' | 'ui_click' | 'ui_toggle' | 'ui_hover' | 'card_deal' | 'card_play' | 'card_draw' | 'card_collect' | 'shuffle' | 'card_shuffle' | 'event_pisti' | 'event_round_win' | 'event_game_win' | 'event_lose' | 'tile_drop' | 'tile_draw' | 'tile_shuffle' | 'tile_deal' | 'turn_start' | 'timeout_warning' | 'error' | 'game_start' | 'event_strong_play' | 'emoji_applause' | 'emoji_angry' | 'emoji_surprised' | 'emoji_laughing' | 'emoji_lucky' | 'emoji_pop') {
    this.triggerHaptic(soundName);
    if (this.muted) return;
    this.initCtx();
    if (!this.audioCtx) return;

    const t = this.audioCtx.currentTime;

    switch (soundName) {
      // Emoji reaction sounds
      case 'emoji_applause':
        this.playEmojiApplause(t);
        break;
      case 'emoji_angry':
        this.playEmojiAngry(t);
        break;
      case 'emoji_surprised':
        this.playEmojiSurprised(t);
        break;
      case 'emoji_laughing':
        this.playEmojiLaughing(t);
        break;
      case 'emoji_lucky':
        this.playEmojiLucky(t);
        break;
      case 'emoji_pop':
        this.playEmojiPop(t);
        break;
      // Compatibility with previous names
      case 'click':
      case 'ui_click':
        this.playUiClick(t);
        break;
      case 'ui_toggle':
        this.playUiToggle(t);
        break;
      case 'ui_hover':
        this.playUiHover(t);
        break;
      case 'shuffle':
      case 'card_shuffle':
        this.playCardShuffle(t);
        break;
      case 'deal':
        // Kart dağıtma: 4 kart dağıtma ses efekti
        for (let i = 0; i < 4; i++) {
          this.playPhysicalCardDeal(i, i * 0.08);
        }
        break;
      case 'card_deal':
        this.playPhysicalCardDeal(0, 0);
        break;
      case 'cardPlay':
      case 'card_play':
      case 'event_strong_play':
        this.playCardPlay(t);
        break;
      case 'card_draw':
        this.playCardDraw(t);
        break;
      case 'collect':
      case 'card_collect':
        this.playCardCollect(t);
        break;
      case 'pisti':
      case 'event_pisti':
        this.playEventPisti(t);
        break;
      case 'event_round_win':
        this.playEventRoundWin(t);
        break;
      case 'win':
      case 'event_game_win':
        this.playEventGameWin(t);
        break;
      case 'lose':
      case 'event_lose':
        this.playEventLose(t);
        break;
      case 'tile_drop':
        this.playTileDrop(t);
        break;
      case 'tile_draw':
        this.playTileDraw(t);
        break;
      case 'tile_shuffle':
        this.playTileShuffle(t);
        break;
      case 'tile_deal':
        this.playTileDrop(t);
        break;
      case 'turn_start':
        this.playTurnStart(t);
        break;
      case 'timeout_warning':
        this.playTimeoutWarning(t);
        break;
      case 'error':
        this.playError(t);
        break;
      case 'game_start':
        this.playGameStart(t);
        break;
    }
  }

  // --- UI SOUNDS --- //

  // Clean, modern, very short tap
  private playUiClick(t: number) {
    if (!this.audioCtx) return;
    const osc = this.audioCtx.createOscillator();
    const gain = this.audioCtx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(1000, t);
    osc.frequency.exponentialRampToValueAtTime(300, t + 0.03);
    gain.gain.setValueAtTime(0.12, t);
    gain.gain.exponentialRampToValueAtTime(0.01, t + 0.03);
    osc.connect(gain);
    gain.connect(this.getMasterNode());
    osc.start(t);
    osc.stop(t + 0.04);
  }

  // Soft "tick" or "pop"
  private playUiToggle(t: number) {
    if (!this.audioCtx) return;
    const osc = this.audioCtx.createOscillator();
    const gain = this.audioCtx.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(600, t);
    osc.frequency.exponentialRampToValueAtTime(200, t + 0.04);
    gain.gain.setValueAtTime(0.10, t);
    gain.gain.exponentialRampToValueAtTime(0.01, t + 0.04);
    osc.connect(gain);
    gain.connect(this.getMasterNode());
    osc.start(t);
    osc.stop(t + 0.05);
  }

  // Very subtle micro-feedback
  private playUiHover(t: number) {
    if (!this.audioCtx) return;
    const osc = this.audioCtx.createOscillator();
    const gain = this.audioCtx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(1200, t);
    gain.gain.setValueAtTime(0.02, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.02);
    osc.connect(gain);
    gain.connect(this.getMasterNode());
    osc.start(t);
    osc.stop(t + 0.03);
  }

  // --- CARD SOUNDS --- //

  // Generates generic noise bursts for textures
  private createNoiseBurst(t: number, duration: number, freq: number, Q: number, vol: number, type: 'bandpass' | 'highpass' | 'lowpass' = 'bandpass') {
    if (!this.audioCtx) return;
    const bufferSize = this.audioCtx.sampleRate * Math.max(duration, 0.01);
    const buffer = this.audioCtx.createBuffer(1, bufferSize, this.audioCtx.sampleRate);
    const data = buffer.getChannelData(0);
    
    for (let i = 0; i < bufferSize; i++) {
        data[i] = Math.random() * 2 - 1;
    }
    const noise = this.audioCtx.createBufferSource();
    noise.buffer = buffer;
    
    const filter = this.audioCtx.createBiquadFilter();
    filter.type = type;
    filter.frequency.setValueAtTime(freq, t);
    filter.Q.value = Q;
    
    const gain = this.audioCtx.createGain();
    const attack = Math.min(0.005, duration * 0.2); // safe attack time
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(vol, t + attack);
    gain.gain.exponentialRampToValueAtTime(0.001, t + duration);
    
    noise.connect(filter);
    filter.connect(gain);
    gain.connect(this.getMasterNode());
    noise.start(t);
  }

  /**
   * Main central card sound method (oxidvideos-taking-playing-card-522520.mp3).
   * Low latency Web Audio API execution with preloaded AudioBuffer.
   * Can be used directly for card deal, user card play, bot card play, opponent card play.
   */
  public playCardSound(delaySeconds: number = 0, volume: number = 0.38, playbackRate: number = 1.22) {
    if (this.muted) return;
    this.initCtx();
    if (!this.audioCtx) return;
    const t = this.audioCtx.currentTime + delaySeconds;
    const rate = playbackRate * (0.98 + Math.random() * 0.04);
    this.playBuffer('takingCard', t, volume, rate);
  }

  // Full deck shuffle sound
  private playCardShuffle(t: number) {
    const played = this.playBuffer('shuffling', t, 0.32);
    if (!played) {
      const freq = 1100 + Math.random() * 200;
      this.createNoiseBurst(t, 0.2, freq, 1.0, 1.0, 'bandpass');
    }
  }

  /**
   * Pure physical card deal acoustics.
   * Masadaki kâğıt dağıtımında kısa, tok ve net temas sesi.
   * Uzayan sürtünme ("sııssss") ve beyaz gürültü tamamen kaldırıldı.
   */
  public playPhysicalCardDeal(
    _cardIndex: number = 0,
    delaySeconds: number = 0,
    targetType: "player" | "middle" | "opponent" = "player",
    isTheatrical: boolean = false,
    pan: number = 0
  ) {
    if (this.muted) return;
    this.initCtx();
    if (!this.audioCtx) return;
    const t = this.audioCtx.currentTime + delaySeconds;
    const isPlayer = targetType === "player" || isTheatrical;
    const isMiddle = targetType === "middle";

    // Doğal hız varyansı (+/- %1.5)
    const naturalRate = 1.06 + (Math.random() * 0.04 - 0.02);
    // Kısık, yumuşak ve dinlendirici kâğıt temas seviyesi
    const volume = isTheatrical ? 0.35 : isPlayer ? 0.28 : isMiddle ? 0.20 : 0.14;

    // Gerçek takingCard sesinin ilk 65ms'lik temiz, tok vuruşu - sıss sesi ve uzayan sürtünme kesinlikle yok
    const played = this.playBuffer("takingCard", t, volume, naturalRate, true, pan, 0.065);
    if (!played && this.audioCtx) {
      this.createCardFrictionNoise(t, 0.025, volume * 0.25);
    }
  }

  // Geriye dönük uyumluluk için alias
  public playCardDealWithPitch(
    _pitchFactor: number = 1.0,
    delaySeconds: number = 0,
    targetType: "player" | "middle" | "opponent" = "player",
    isTheatrical: boolean = false,
    pan: number = 0
  ) {
    this.playPhysicalCardDeal(0, delaySeconds, targetType, isTheatrical, pan);
  }

  public playCardDeal(delaySeconds: number = 0) {
    this.playPhysicalCardDeal(0, delaySeconds);
  }

  // Elde kartlar arasında parmak gezdirirken çıkan şık şık sesi tamamen kaldırıldı
  public playCardHover() {
    // Sessiz ve pürüzsüz
  }

  // Deste oturma sesi: Masaya oturan deste kartlarının tok fiziksel dokunuşu
  public playDeckSettle(delaySeconds: number = 0) {
    if (this.muted) return;
    this.initCtx();
    if (!this.audioCtx) return;
    const t = this.audioCtx.currentTime + delaySeconds;
    // Tek, temiz ve tok kâğıt oturması - uzayan sürtünme veya sıss sesi yok
    this.playBuffer("takingCard", t, 0.30, 0.96, true, 0, 0.10);
  }

  /**
   * Tamamen perdesiz, rezonanssız gerçek kâğıt/keçe sürtünme dokusu üretir.
   * Zil, çan, ıslık veya ton oluşturmaz.
   */
  private createCardFrictionNoise(t: number, duration: number, vol: number) {
    if (!this.audioCtx) return;
    const bufferSize = Math.floor(this.audioCtx.sampleRate * Math.max(duration, 0.01));
    const buffer = this.audioCtx.createBuffer(1, bufferSize, this.audioCtx.sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }
    const noise = this.audioCtx.createBufferSource();
    noise.buffer = buffer;

    // 300Hz altındaki uğultuyu kesen ve 2400Hz üstündeki metalik frekansları süzen doğal filtre
    const hp = this.audioCtx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.setValueAtTime(300, t);
    hp.Q.value = 0.7;

    const lp = this.audioCtx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.setValueAtTime(2400, t);
    lp.Q.value = 0.7;

    const gain = this.audioCtx.createGain();
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(vol, t + 0.003);
    gain.gain.exponentialRampToValueAtTime(0.001, t + duration);

    noise.connect(hp);
    hp.connect(lp);
    lp.connect(gain);
    gain.connect(this.getMasterNode());
    noise.start(t);
  }

  // Tactile haptic feedback during dealing
  public triggerDealHaptic(isLastCard: boolean = false) {
    if (!this.hapticsEnabled || typeof navigator === "undefined" || !navigator.vibrate) return;
    try {
      if (isLastCard) {
        navigator.vibrate([16, 25, 20]);
      } else {
        navigator.vibrate(8);
      }
    } catch {}
  }

  // Card Play Sound (masadaki keçeye tok oturma vuruşu - şık/çık çıtırtısı tamamen kaldırıldı)
  private playCardPlay(t: number) {
    // Masanın keçesine kâğıdın tek, tok ve yumuşak oturma vuruşu ("tok")
    const landingRate = 0.99 + (Math.random() * 0.03 - 0.015);
    const played = this.playBuffer("takingCard", t, 0.38, landingRate, true, 0);
    if (!played && this.audioCtx) {
      this.createCardFrictionNoise(t, 0.035, 0.20);
    }
  }

  // Card Draw Sound
  private playCardDraw(t: number) {
    const rate = 1.22 + Math.random() * 0.04;
    const played = this.playBuffer('takingCard', t, 0.35, rate);
    if (!played && this.audioCtx) {
      this.createNoiseBurst(t, 0.04, 2200, 0.5, 0.25);
    }
  }

  // Satisfying grouped "stack gather" sound + 2 variations
  private playCardCollect(t: number) {
    if (!this.audioCtx) return;
    this.cardCollectCounter = (this.cardCollectCounter + 1) % 2;
    
    const count = 1; // Just 1 scoop to avoid sounding like a card shuffle
    for(let j=0; j<count; j++) {
       const startTime = t + j * 0.05;
       const freqOffset = this.cardCollectCounter === 0 ? 100 : -100;
       this.createNoiseBurst(startTime, 0.12, 600 + j*150 + freqOffset, 1.2, 0.30);
    }
  }

  // --- OKEY TILE SOUNDS --- //

  // Hard plastic / melamine tile hitting a wooden rack or table - REALISTIC
  private playTileDrop(t: number) {
    if (!this.audioCtx) return;
    
    const varPitch = 1.0 + (Math.random() * 0.06 - 0.03);

    // 1. Low Thud (Wood/Table resonance)
    const thud = this.audioCtx.createOscillator();
    const thudGain = this.audioCtx.createGain();
    thud.type = 'sine';
    thud.frequency.setValueAtTime(120 * varPitch, t);
    thud.frequency.exponentialRampToValueAtTime(30, t + 0.12);
    thudGain.gain.setValueAtTime(0, t);
    thudGain.gain.linearRampToValueAtTime(0.7, t + 0.01);
    thudGain.gain.exponentialRampToValueAtTime(0.01, t + 0.15);
    thud.connect(thudGain);
    thudGain.connect(this.getMasterNode());
    thud.start(t);
    thud.stop(t + 0.16);

    // 2. Hard Plastic Clack (Sharp transient)
    const clack = this.audioCtx.createOscillator();
    const clackGain = this.audioCtx.createGain();
    clack.type = 'square';
    clack.frequency.setValueAtTime(2500 * varPitch, t);
    clack.frequency.exponentialRampToValueAtTime(400, t + 0.02);
    clackGain.gain.setValueAtTime(0, t);
    clackGain.gain.linearRampToValueAtTime(0.5, t + 0.002);
    clackGain.gain.exponentialRampToValueAtTime(0.01, t + 0.03);
    
    // Slight filtering to make it sound like thick plastic/melamine
    const clackFilter = this.audioCtx.createBiquadFilter();
    clackFilter.type = 'bandpass';
    clackFilter.frequency.value = 1800;
    clackFilter.Q.value = 0.8;
    clack.connect(clackFilter);
    clackFilter.connect(clackGain);
    clackGain.connect(this.getMasterNode());
    
    clack.start(t);
    clack.stop(t + 0.04);

    // 3. Very subtle secondary rattle (tiles hitting each other slightly)
    setTimeout(() => {
        if (!this.audioCtx) return;
        const tt = this.audioCtx.currentTime;
        const rattle = this.audioCtx.createOscillator();
        const rattleGain = this.audioCtx.createGain();
        rattle.type = 'triangle';
        rattle.frequency.setValueAtTime(1000 * varPitch, tt);
        rattle.frequency.exponentialRampToValueAtTime(500, tt + 0.01);
        rattleGain.gain.setValueAtTime(0, tt);
        rattleGain.gain.linearRampToValueAtTime(0.15, tt + 0.001);
        rattleGain.gain.exponentialRampToValueAtTime(0.01, tt + 0.02);
        rattle.connect(rattleGain);
        rattleGain.connect(this.getMasterNode());
        rattle.start(tt);
        rattle.stop(tt + 0.03);
    }, 15 + Math.random() * 10);
  }

  // Sliding a plastic tile across a table or picking it up
  private playTileDraw(t: number) {
    if (!this.audioCtx) return;
    
    const varPitch = 1.0 + (Math.random() * 0.1 - 0.05);

    // Initial grab clack (very short, quiet)
    const oscInitial = this.audioCtx.createOscillator();
    const gainInitial = this.audioCtx.createGain();
    oscInitial.type = 'triangle';
    oscInitial.frequency.setValueAtTime(1500 * varPitch, t);
    oscInitial.frequency.exponentialRampToValueAtTime(800 * varPitch, t + 0.015);
    
    gainInitial.gain.setValueAtTime(0, t);
    gainInitial.gain.linearRampToValueAtTime(0.1, t + 0.002);
    gainInitial.gain.exponentialRampToValueAtTime(0.001, t + 0.02);
    
    oscInitial.connect(gainInitial);
    gainInitial.connect(this.getMasterNode());
    oscInitial.start(t);
    oscInitial.stop(t + 0.03);

    // Friction / Slide noise (longer, softer)
    this.createNoiseBurst(t + 0.01, 0.08, 1500, 1.0, 0.3, 'bandpass');
    
    // Pick-up/clack at the end of the slide
    const osc = this.audioCtx.createOscillator();
    const gain = this.audioCtx.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(800 * varPitch, t + 0.08);
    osc.frequency.exponentialRampToValueAtTime(300 * varPitch, t + 0.10);
    
    gain.gain.setValueAtTime(0, t + 0.08);
    gain.gain.linearRampToValueAtTime(0.25, t + 0.085);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.12);

    osc.connect(gain);
    gain.connect(this.getMasterNode());
    osc.start(t + 0.08);
    osc.stop(t + 0.13);
  }

  // Generate a cascade of 8-10 soft, random clacks representing tile shuffling
  private playTileShuffle(t: number) {
    if (!this.audioCtx) return;
    const now = Date.now();
    if (now - this.lastShuffleTime < 1000) return;
    this.lastShuffleTime = now;
    for (let i = 0; i < 10; i++) {
      const startTime = t + i * 0.12 + Math.random() * 0.05;
      const vol = 0.15 + Math.random() * 0.15;
      const varPitch = 0.8 + Math.random() * 0.4;
      
      // Short triangle thud
      const osc = this.audioCtx.createOscillator();
      const gain = this.audioCtx.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(250 * varPitch, startTime);
      osc.frequency.exponentialRampToValueAtTime(30 * varPitch, startTime + 0.08);
      
      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(vol, startTime + 0.002);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.07);
      
      osc.connect(gain);
      gain.connect(this.getMasterNode());
      osc.start(startTime);
      osc.stop(startTime + 0.08);

      // Sharp transient
      const oscHigh = this.audioCtx.createOscillator();
      const gainHigh = this.audioCtx.createGain();
      oscHigh.type = 'sine';
      oscHigh.frequency.setValueAtTime(3000 * varPitch, startTime);
      oscHigh.frequency.exponentialRampToValueAtTime(700 * varPitch, startTime + 0.015);
      
      gainHigh.gain.setValueAtTime(0, startTime);
      gainHigh.gain.linearRampToValueAtTime(vol * 0.5, startTime + 0.001);
      gainHigh.gain.exponentialRampToValueAtTime(0.001, startTime + 0.02);
      
      oscHigh.connect(gainHigh);
      gainHigh.connect(this.getMasterNode());
      oscHigh.start(startTime);
      oscHigh.stop(startTime + 0.025);
    }
    // Friction background noise
    this.createNoiseBurst(t, 1.2, 1000, 0.5, 0.15, 'lowpass');
  }

  // --- EVENT SOUNDS --- //

  // Kısa Islık (Short whistle) for Pişti
  private playEventPisti(t: number) {
    if (!this.audioCtx) return;
    
    const freqStart = 1200 + Math.random() * 100;
    const freqMid = 2400 + Math.random() * 200;
    const freqEnd = 1500 + Math.random() * 100;

    // 1. Pure Whistle Tone (Sine)
    const osc = this.audioCtx.createOscillator();
    const gain = this.audioCtx.createGain();
    osc.type = 'sine';
    
    // Pitch envelope
    osc.frequency.setValueAtTime(freqStart, t);
    osc.frequency.exponentialRampToValueAtTime(freqMid, t + 0.1);
    osc.frequency.exponentialRampToValueAtTime(freqEnd, t + 0.25);

    // Volume envelope
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.3, t + 0.05);
    gain.gain.setValueAtTime(0.3, t + 0.1);
    gain.gain.exponentialRampToValueAtTime(0.01, t + 0.25);

    osc.connect(gain);
    gain.connect(this.getMasterNode());
    osc.start(t);
    osc.stop(t + 0.3);

    // 2. Air Noise (Simulates breath)
    // We'll create a noise burst that follows the frequency of the oscillator
    const noiseBuffer = this.audioCtx.createBuffer(1, this.audioCtx.sampleRate * 0.3, this.audioCtx.sampleRate);
    const noiseData = noiseBuffer.getChannelData(0);
    for (let i = 0; i < noiseData.length; i++) noiseData[i] = Math.random() * 2 - 1;
    
    const noiseSource = this.audioCtx.createBufferSource();
    noiseSource.buffer = noiseBuffer;
    
    const noiseFilter = this.audioCtx.createBiquadFilter();
    noiseFilter.type = 'bandpass';
    noiseFilter.Q.value = 10;
    noiseFilter.frequency.setValueAtTime(freqStart, t);
    noiseFilter.frequency.exponentialRampToValueAtTime(freqMid, t + 0.1);
    noiseFilter.frequency.exponentialRampToValueAtTime(freqEnd, t + 0.25);

    const noiseGain = this.audioCtx.createGain();
    noiseGain.gain.setValueAtTime(0, t);
    noiseGain.gain.linearRampToValueAtTime(0.1, t + 0.05);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, t + 0.25);

    noiseSource.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(this.getMasterNode());
    noiseSource.start(t);
  }

  // Soft success tone (short/casual)
  private playEventRoundWin(t: number) {
    if (!this.audioCtx) return;
    [523.25, 783.99].forEach((f, i) => { // C5, G5
      const osc = this.audioCtx!.createOscillator();
      const gain = this.audioCtx!.createGain();
      osc.type = 'triangle';
      osc.frequency.value = f;
      const startTime = t + i * 0.1;

      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(0.2, startTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.3);

      osc.connect(gain);
      gain.connect(this.getMasterNode());
      osc.start(startTime);
      osc.stop(startTime + 0.4);
    });
  }

  // Slightly richer success sound, minimal (Game Win)
  private playEventGameWin(t: number) {
    if (!this.audioCtx) return;
    const notes = [
      { f: 523.25, time: 0 },    // C5
      { f: 659.25, time: 0.1 },  // E5
      { f: 783.99, time: 0.2 },  // G5
      { f: 1046.50, time: 0.35 } // C6 (held slightly longer)
    ];

    notes.forEach((note, i) => {
      const osc = this.audioCtx!.createOscillator();
      const gain = this.audioCtx!.createGain();
      osc.type = 'sine';
      osc.frequency.value = note.f;

      const startTime = t + note.time;
      const dur = i === 3 ? 0.6 : 0.2;
      
      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(0.3, startTime + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + dur);

      osc.connect(gain);
      gain.connect(this.getMasterNode());
      osc.start(startTime);
      osc.stop(startTime + dur + 0.1);
    });
  }

  // Subtle, soft negative tone
  private playEventLose(t: number) {
    if (!this.audioCtx) return;
    const notes = [
      { f: 349.23, time: 0 },   // F4
      { f: 311.13, time: 0.15 }, // Eb4
      { f: 261.63, time: 0.35 } // C4
    ];

    notes.forEach((note, i) => {
      const osc = this.audioCtx!.createOscillator();
      const gain = this.audioCtx!.createGain();
      osc.type = 'triangle';
      
      const startTime = t + note.time;
      const dur = i === 2 ? 0.6 : 0.2;
      
      // Slight pitch droop for sadness
      osc.frequency.setValueAtTime(note.f, startTime);
      osc.frequency.exponentialRampToValueAtTime(note.f * 0.95, startTime + dur);

      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(0.2, startTime + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + dur);

      // Lowpass to make it muffled
      const filter = this.audioCtx!.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 800;

      osc.connect(filter);
      filter.connect(gain);
      gain.connect(this.getMasterNode());
      
      osc.start(startTime);
      osc.stop(startTime + dur + 0.1);
    });
  }

  // Soft attention tone for "Your turn"
  private playTurnStart(t: number) {
    if (!this.audioCtx) return;
    const osc = this.audioCtx.createOscillator();
    const gain = this.audioCtx.createGain();
    
    osc.type = 'sine';
    osc.frequency.setValueAtTime(440, t); // A4
    osc.frequency.exponentialRampToValueAtTime(880, t + 0.1); // Quick jump to A5
    
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.3, t + 0.05);
    gain.gain.exponentialRampToValueAtTime(0.01, t + 0.4);

    osc.connect(gain);
    gain.connect(this.getMasterNode());
    
    osc.start(t);
    osc.stop(t + 0.5);
  }

  // Ticking sound for timeout warning
  private playTimeoutWarning(t: number) {
    if (!this.audioCtx) return;
    const osc = this.audioCtx.createOscillator();
    const gain = this.audioCtx.createGain();
    
    osc.type = 'square';
    osc.frequency.setValueAtTime(800, t);
    
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.1, t + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.1);
    
    // Lowpass filter to make it sound like a metronome click
    const filter = this.audioCtx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 1000;
    
    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.getMasterNode());
    
    osc.start(t);
    osc.stop(t + 0.15);
  }

  // Low error thud 
  private playError(t: number) {
    if (!this.audioCtx) return;
    const osc = this.audioCtx.createOscillator();
    const gain = this.audioCtx.createGain();
    
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(150, t);
    osc.frequency.exponentialRampToValueAtTime(50, t + 0.2);
    
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.15, t + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.01, t + 0.2);
    
    const filter = this.audioCtx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(800, t);
    filter.frequency.exponentialRampToValueAtTime(100, t + 0.2);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.getMasterNode());
    
    osc.start(t);
    osc.stop(t + 0.3);
  }

  // A nice ascending chime for game start
  private playGameStart(t: number) {
    if (!this.audioCtx) return;
    const notes = [
      { f: 523.25, time: 0 },    // C5
      { f: 659.25, time: 0.1 },  // E5
      { f: 783.99, time: 0.2 },  // G5
      { f: 1046.50, time: 0.35 } // C6
    ];

    notes.forEach((note, i) => {
      const osc = this.audioCtx!.createOscillator();
      const gain = this.audioCtx!.createGain();
      osc.type = 'sine';
      osc.frequency.value = note.f;

      const startTime = t + note.time;
      const dur = i === 3 ? 1.0 : 0.3; // Last note rings out longer
      
      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(0.2, startTime + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + dur);

      osc.connect(gain);
      gain.connect(this.getMasterNode());
      osc.start(startTime);
      osc.stop(startTime + dur + 0.1);
    });
  }

  // 👏 Alkış (Applause - rhythmic clapping bursts)
  private playEmojiApplause(t: number) {
    if (!this.audioCtx) return;
    const clapDelays = [0, 0.08, 0.17, 0.27, 0.38, 0.50];
    clapDelays.forEach((delay) => {
      const startTime = t + delay;
      // White noise burst with bandpass for slap
      const buffer = this.audioCtx!.createBuffer(1, this.audioCtx!.sampleRate * 0.06, this.audioCtx!.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (data.length * 0.25));

      const source = this.audioCtx!.createBufferSource();
      source.buffer = buffer;

      const filter = this.audioCtx!.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = 1200 + Math.random() * 400;
      filter.Q.value = 2.5;

      const gain = this.audioCtx!.createGain();
      gain.gain.setValueAtTime(0.35, startTime);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.05);

      source.connect(filter);
      filter.connect(gain);
      gain.connect(this.getMasterNode());
      source.start(startTime);
    });
  }

  // 😡 Kızgın (Angry - low buzz rumble)
  private playEmojiAngry(t: number) {
    if (!this.audioCtx) return;
    const osc = this.audioCtx.createOscillator();
    const gain = this.audioCtx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(120, t);
    osc.frequency.linearRampToValueAtTime(75, t + 0.35);

    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.28, t + 0.03);
    gain.gain.exponentialRampToValueAtTime(0.01, t + 0.4);

    const filter = this.audioCtx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(600, t);
    filter.frequency.linearRampToValueAtTime(200, t + 0.35);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.getMasterNode());
    osc.start(t);
    osc.stop(t + 0.45);
  }

  // 😲 Şaşkın (Surprised - rising slide whistle / gasp)
  private playEmojiSurprised(t: number) {
    if (!this.audioCtx) return;
    const osc = this.audioCtx.createOscillator();
    const gain = this.audioCtx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(320, t);
    osc.frequency.exponentialRampToValueAtTime(950, t + 0.28);

    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.3, t + 0.04);
    gain.gain.exponentialRampToValueAtTime(0.01, t + 0.35);

    osc.connect(gain);
    gain.connect(this.getMasterNode());
    osc.start(t);
    osc.stop(t + 0.4);
  }

  // 😂 Gülen (Laughing - bouncy chuckle arpeggio)
  private playEmojiLaughing(t: number) {
    if (!this.audioCtx) return;
    const notes = [
      { f: 580, time: 0 },
      { f: 520, time: 0.09 },
      { f: 650, time: 0.18 },
      { f: 550, time: 0.27 },
      { f: 720, time: 0.36 }
    ];

    notes.forEach((note) => {
      const osc = this.audioCtx!.createOscillator();
      const gain = this.audioCtx!.createGain();
      osc.type = 'triangle';
      osc.frequency.value = note.f;

      const startTime = t + note.time;
      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(0.25, startTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.11);

      osc.connect(gain);
      gain.connect(this.getMasterNode());
      osc.start(startTime);
      osc.stop(startTime + 0.14);
    });
  }

  // 🍀 Şanslı (Lucky - sparkling lucky coin jackpot cascade)
  private playEmojiLucky(t: number) {
    if (!this.audioCtx) return;
    const chords = [523.25, 659.25, 783.99, 1046.50, 1318.51]; // C5, E5, G5, C6, E6
    chords.forEach((f, i) => {
      const osc = this.audioCtx!.createOscillator();
      const gain = this.audioCtx!.createGain();
      osc.type = 'sine';
      osc.frequency.value = f;

      const startTime = t + i * 0.06;
      const dur = 0.45;
      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(0.22, startTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.005, startTime + dur);

      osc.connect(gain);
      gain.connect(this.getMasterNode());
      osc.start(startTime);
      osc.stop(startTime + dur + 0.05);
    });
  }

  // Bubble pop when emoji is dropped
  private playEmojiPop(t: number) {
    if (!this.audioCtx) return;
    const osc = this.audioCtx.createOscillator();
    const gain = this.audioCtx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(600, t);
    osc.frequency.exponentialRampToValueAtTime(1400, t + 0.04);

    gain.gain.setValueAtTime(0.25, t);
    gain.gain.exponentialRampToValueAtTime(0.01, t + 0.08);

    osc.connect(gain);
    gain.connect(this.getMasterNode());
    osc.start(t);
    osc.stop(t + 0.1);
  }
}

export const soundManager = new SoundManager();
